/**
 * 003 — Übersicht: the prototype's bank basis, months, receivables, document
 * overview and statement provenance (moved without behaviour change) plus the
 * cashflow blocks of FR-040..FR-043 with their reconciliation line.
 */
import { useQuery } from '@tanstack/react-query';
import { GlassPanel, LoadingSpinner } from '../ui';
import { ApiError } from '../../services/api';
import { bankKeys, money, day, ibanLabel, type MonthBlocks } from '../../lib/bank-api';
import { api } from '../../services/api';
import { errorText } from './ReviewEditor';
import type { BankTabProps, Flow, FlowDocument } from './types';

export function useCashflow(p: Pick<BankTabProps, 'tenantId' | 'from' | 'to' | 'basis' | 'account'>) {
  return useQuery({
    queryKey: bankKeys.cashflow(p.tenantId, p.from, p.to, p.basis, p.account),
    queryFn: () =>
      api.get<Flow>(
        `/bank/cashflow?${new globalThis.URLSearchParams({
          from: p.from,
          to: p.to,
          dateBasis: p.basis,
          ...(p.account ? { account: p.account } : {}),
        })}`
      ),
    enabled: Boolean(p.tenantId),
    // API errors (incl. 500 CASHFLOW_INVARIANT) are deterministic; only network errors are retried once.
    retry: (count, error) => !(error instanceof ApiError) && count < 1,
  });
}

const sum = (r: Record<string, number>) => Object.values(r).reduce((a, v) => a + v, 0);

function BlocksPanel({ data }: { data: Flow }) {
  const total = data.blocks.total;
  const categoryName = (key: string) =>
    key === 'bank_fee'
      ? 'Bankentgelte'
      : key === 'uncategorized'
        ? 'Ohne Kategorie'
        : key === 'unassigned_business'
          ? 'Geschäftlich, ohne Beleg'
          : (data.categories.find((c) => c.id === key)?.name ?? key);
  const inName = (key: string) =>
    key === 'invoice' ? 'Rechnungen' : key === 'payout' ? 'Auszahlungen Zahlungsdienstleister' : 'Geschäftlich, ohne Beleg';
  const partnerName = (key: string) =>
    key === 'unknown_partner' ? 'Gesellschafter unbekannt' : (data.partners.find((p) => p.id === key)?.displayName ?? key);
  const rows = (m: MonthBlocks) => [
    { label: 'Geschäftlich Eingänge', cents: sum(m.businessIn) },
    { label: 'Geschäftlich Ausgänge', cents: sum(m.businessOut) },
    { label: 'Gesellschafter (netto)', cents: Object.values(m.partners).reduce((a, p) => a + p.in - p.out, 0) },
    { label: 'Steuern', cents: m.tax },
    { label: 'Umbuchungen / Rücklastschriften', cents: m.transfer },
    { label: `Nicht zugeordnet (${m.unassigned.count})`, cents: m.unassigned.cents },
  ];
  return (
    <GlassPanel data-testid="blocks-panel">
      <h2 className="text-lg font-semibold">Cashflow nach Bereichen</h2>
      <p className="text-sm text-white/70 my-2">
        Nur bestätigte Zuordnungen zählen; offene Buchungen stehen unter „nicht zugeordnet“. Keine Steuerberechnung.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <section data-testid="blocks-business-in">
          <h3 className="font-medium">Eingänge geschäftlich</h3>
          <ul className="text-sm">
            {Object.entries(total.businessIn).map(([k, v]) => (
              <li key={k} className="flex justify-between gap-2">
                <span>{inName(k)}</span>
                <span>{money(v)}</span>
              </li>
            ))}
            {Object.keys(total.businessIn).length === 0 && <li className="text-white/50">Keine</li>}
          </ul>
        </section>
        <section data-testid="blocks-business-out">
          <h3 className="font-medium">Ausgänge geschäftlich nach Kategorie</h3>
          <ul className="text-sm">
            {Object.entries(total.businessOut)
              .sort((a, b) => a[1] - b[1])
              .map(([k, v]) => (
                <li key={k} className="flex justify-between gap-2">
                  <span>{categoryName(k)}</span>
                  <span>{money(v)}</span>
                </li>
              ))}
            {Object.keys(total.businessOut).length === 0 && <li className="text-white/50">Keine</li>}
          </ul>
        </section>
        <section data-testid="blocks-partners">
          <h3 className="font-medium">Gesellschafter</h3>
          <ul className="text-sm">
            {Object.entries(total.partners).map(([k, p]) => (
              <li key={k} className="flex justify-between gap-2">
                <span>{partnerName(k)}</span>
                <span>
                  Einlagen {money(p.in)} · Entnahmen/Erstattungen {money(p.out)}
                </span>
              </li>
            ))}
            {Object.keys(total.partners).length === 0 && <li className="text-white/50">Keine</li>}
          </ul>
        </section>
        <section data-testid="blocks-other">
          <h3 className="font-medium">Steuern, Umbuchungen, offen</h3>
          <ul className="text-sm">
            <li className="flex justify-between gap-2">
              <span>Steuerzahlungen</span>
              <span>{money(total.tax)}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span>Umbuchungen / Rücklastschriften</span>
              <span>{money(total.transfer)}</span>
            </li>
            <li className="flex justify-between gap-2" data-testid="blocks-unassigned">
              <span>Nicht zugeordnet ({total.unassigned.count} Buchungen)</span>
              <span>{money(total.unassigned.cents)}</span>
            </li>
          </ul>
        </section>
      </div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left">Monat</th>
              {rows(total).map((r) => (
                <th key={r.label} className="text-right">
                  {r.label.replace(/ \(\d+\)$/, '')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.blocks.months.map((m) => (
              <tr key={m.month}>
                <td>{m.month}</td>
                {rows(m).map((r) => (
                  <td key={r.label} className="text-right">
                    {money(r.cents)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm" data-testid="blocks-reconciliation-ok">
        Summe aller Blöcke {money(data.reconciliation.blocksSumCents)} = Nettobewegung laut Kontoauszug{' '}
        {money(data.reconciliation.bankNetCents)} ✓
      </p>
    </GlassPanel>
  );
}

function DocumentTable({ documents }: { documents: FlowDocument[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th>Art / Beleg</th>
            <th>Rechnungstag</th>
            <th>Zahlungstag</th>
            <th>Netto / Brutto / Währung</th>
            <th>Status</th>
            <th>Bankzuordnung EUR</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((d) => (
            <tr key={`${d.kind}:${d.id}`}>
              <td>
                {d.kind === 'invoice' ? 'Rechnung' : 'Ausgabe'} {d.number ?? '—'}
              </td>
              <td>{d.invoiceDate ? day(d.invoiceDate) : 'Unbekannt'}</td>
              <td>{d.paymentDate ? day(d.paymentDate) : 'Unbekannt'}</td>
              <td>
                {d.netTotal} / {d.grossTotal} {d.currency}
              </td>
              <td>{d.status}</td>
              <td>{d.linkedCents ? money(d.linkedCents) : 'Offen'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {documents.length === 0 && <p>Keine zeitlich zuordenbaren Belege.</p>}
    </div>
  );
}

export function OverviewTab(props: BankTabProps) {
  const query = useCashflow(props);
  const data = query.data;
  if (query.isLoading) return <LoadingSpinner />;
  if (query.error) {
    const invariant = query.error instanceof ApiError && (query.error.data as { code?: string })?.code === 'CASHFLOW_INVARIANT';
    return (
      <p role="alert" className="rounded border border-red-400/40 p-3" data-testid="blocks-invariant-error">
        {invariant
          ? 'Die Summe der Cashflow-Bereiche weicht von der Nettobewegung laut Kontoauszug ab. Zahlen werden nicht angezeigt — bitte Zuordnungen prüfen.'
          : errorText(query.error)}
      </p>
    );
  }
  if (!data) return null;
  return (
    <div className="space-y-6">
      <GlassPanel>
        <h2 className="text-lg font-semibold">
          Bankbasis ·{' '}
          {data.account ? ibanLabel(data.account) : data.accounts.length > 1 ? 'alle Konten' : 'Noch kein Konto importiert'}
        </h2>
        {data.coverage.length > 0 ? (
          <p className="my-3 text-amber-200">
            Fehlende Auszugsabdeckung: {data.coverage.join('; ')}. Beträge zeigen nur belegte Buchungen; ein vollständiger
            Endsaldo ist nicht ausgewiesen.
          </p>
        ) : (
          <p className="my-3">Auszugsabdeckung vollständig für diesen Zeitraum.</p>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {(
            [
              ['Anfangssaldo', data.openingCents],
              ['Eingänge', data.incomingCents],
              ['Ausgänge', data.outgoingCents],
              ['Nettobewegung', data.netCents],
              ['Endsaldo', data.closingCents],
            ] as const
          ).map(([label, value]) => (
            <div key={label}>
              <div className="text-sm text-white/60">{label}</div>
              <div className="text-lg font-semibold">{money(value)}</div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm">
          {data.unknownClassifications} unklare Klassifikationen · {data.unmatchedCount} Buchungen mit offenem Belegrest.
          Verknüpfte Belege werden dem Kontofluss nicht erneut hinzugerechnet.
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th>Monat</th>
                <th>Anfang</th>
                <th>Eingänge</th>
                <th>Ausgänge</th>
                <th>Netto</th>
                <th>Ende</th>
                <th>Abdeckung</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td>{money(m.openingCents)}</td>
                  <td>{money(m.incomingCents)}</td>
                  <td>{money(m.outgoingCents)}</td>
                  <td>{money(m.netCents)}</td>
                  <td>{money(m.closingCents)}</td>
                  <td>{m.complete ? 'Vollständig' : 'Lücke'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassPanel>
      <BlocksPanel data={data} />
      <GlassPanel>
        <h2 className="text-lg font-semibold">Offene Forderungen · separat vom Cashflow</h2>
        <p className="text-sm mb-3">
          Aktueller Bestand, unabhängig vom gewählten Zeitraum. Offene Rechnungen abzüglich explizit zugeordneter
          Bankbeträge; Status und Teilzahlungen bedürfen des Abgleichs.
        </p>
        {data.openReceivables.length === 0 ? (
          <p>Keine offenen Forderungen.</p>
        ) : (
          <ul>
            {data.openReceivables.map((d) => (
              <li key={d.id}>
                {d.number}: {money(d.remainingCents)} offen · {money(d.allocatedCents)} Bankzuordnung
              </li>
            ))}
          </ul>
        )}
      </GlassPanel>
      <GlassPanel>
        <h2 className="text-lg font-semibold">
          Interne Belegübersicht · {props.basis === 'invoiceDate' ? 'Rechnungsbasis' : 'Zahlungsbasis'}
        </h2>
        <p className="text-sm my-3">
          {data.unknownPaymentDates} Belege mit unbekanntem Zahlungstag (gesamter Bestand); {data.unknownInvoiceDates} mit
          unbekanntem Rechnungsdatum. {data.foreignCurrencyDocuments} Fremdwährungsbelege im Zeitraum: Umrechnung fehlt,
          keine EUR-Summe oder Steuerwerte. Rechnungsentwürfe sind ausgeschlossen; stornierte Originale und signierte
          Gegenbelege bleiben für den historischen Abgleich sichtbar; Ausgabenstatus bleibt sichtbar. Die steuerlich
          maßgebliche Sicht nach Zahlungsdatum steht im Tab „Quartal nach Zahlung“.
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th>Quartal / Teilzeitraum</th>
                <th>Rechnungen Netto / Brutto EUR</th>
                <th>Ausgaben Netto / Brutto EUR</th>
                <th>Entwürfe ausgeschlossen</th>
                <th>Fremdwährung ausgeschlossen</th>
                <th>Ungeprüfte Ausgaben</th>
              </tr>
            </thead>
            <tbody>
              {data.quarterGroups.map((q) => (
                <tr key={q.quarter}>
                  <td>{q.quarter}</td>
                  <td>
                    {money(q.invoice.netCents)} / {money(q.invoice.grossCents)} ({q.invoice.count})
                  </td>
                  <td>
                    {money(q.expense.netCents)} / {money(q.expense.grossCents)} ({q.expense.count})
                  </td>
                  <td>{q.draftCount}</td>
                  <td>{q.foreignCurrencyCount}</td>
                  <td>{q.unreviewedExpenseCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DocumentTable documents={data.documents} />
        {data.unknownDateDocuments.length > 0 && (
          <details open>
            <summary className="mt-4">
              Zeitlich nicht zuordenbar auf dieser Belegachse ({data.unknownDateDocuments.length}; gesamter Bestand)
            </summary>
            <DocumentTable documents={data.unknownDateDocuments} />
          </details>
        )}
      </GlassPanel>
      <GlassPanel>
        <h2 className="font-semibold">Auszugsquellen & Provenienz</h2>
        <ul className="text-sm">
          {data.statements
            .filter((s) => s.periodEnd >= props.from && s.periodStart <= props.to)
            .map((s) => (
              <li className="my-2 break-all" key={s.id}>
                {day(s.periodStart)} – {day(s.periodEnd)} · SHA256 {s.sha256}
                {s.provenance.map((p, i) => (
                  <div key={i}>
                    {p.driveUrl ? (
                      <a href={p.driveUrl} target="_blank" rel="noreferrer">
                        {p.title}
                      </a>
                    ) : (
                      p.title
                    )}
                  </div>
                ))}
              </li>
            ))}
        </ul>
      </GlassPanel>
    </div>
  );
}
